import React, {
  useContext, useEffect, useState, useRef, useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  params, string, arrayOf, QueryProvider,
  shape, useDataModel, extend, number, customType, useLazyDataModel, bool
} from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { DrawerHeader } from '@one-thd/sui-atomic-components';
import { CreateAProjectContent } from '@thd-olt-component-react/project-forms';
import { MyStoreForm, DeliveryZipcodeForm } from '@thd-olt-component-react/project-localization';
import { DefaultScreen } from '../../common/DefaultScreen';
import { MiniPip } from '../../common/mini-pip/MiniPip';
import { AddToContext } from '../../AddToContext';
import {
  SCREENS, HEADER_TEXT, ADD_ITEM_NEW_PROJECT, ADD_ITEMS_PROJECT_NEW_GROUP, ADD_ITEMS_PROJECT_EXISTING_GROUP,
  TRACK, ADD_TO_TYPE, COPY_FROM_LIST, COPY_FROM_PROJECT, ANALYTICS_SELECT_PROJECT, LAST_USED_PROJECT_KEY
} from '../../AddToConstants';
import { useProjects, projectsModel, useProjectDetails, projectDetailsModel } from '../../hooks/useProjects';
import { AddToLoading } from '../../common/addto-loading/AddToloading';
import { SelectProject } from './SelectProject';
import {
  onLocalizationUpdate,
  getDrawerTitle,
  shouldShowBack,
  triggerAnalytics,
  isItemIntent,
  getValidSku
} from '../../AddToHelpers';
import { CopyList } from '../copy-list/CopyList';
import { StarterLists } from '../list/StarterLists';
import { IntentsPreview } from '../../common/intents-preview/IntentsPreview';
import { AILoadingAnimation } from '../../common/ai-list-builder/AILoadingAnimation';
import { BathroomQuestionnaire } from '../../common/ai-list-builder/BathroomQuestionnaire';
import { useAddNewIntentsProject } from '../../hooks/useAddNewIntentsProject';
import { ZeroProjectState } from './ZeroProjectState';
import { CreateProjectPreview } from './CreateProjectPreview';

const getMiniPipBackTarget = (products) => (
  products?.length > 0 ? SCREENS.PROJECT_SELECT_PROJECT : SCREENS.DEFAULT
);

export const AddToProject = ({
  projectId,
  products: productsProp = [],
  initialLoad,
  setInitialLoad,
  showBackButton,
  productPodItemData,
  loadedProductsData,
  isDisplayOnly,
}) => {
  const {
    storeId,
    screen,
    setScreen,
    handleClose,
    selectedGroup,
    setSelectedGroup,
    selectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    setSelectedProductDetails,
    addToType,
    atcDrawerType,
    setAtcDrawerType,
    previousScreen,
    copyListToNewProject,
    onProjectCreated
  } = useContext(AddToContext);
  // Flag to fire analytic if an item has been added to a project with a new project
  const isProjectCreated = useRef(false);
  // Track if selectedProject was changed by user action (not initial prop)
  const userSelectedProject = useRef(false);

  const isIntent = Boolean(!productPodItemData?.itemId && productPodItemData?.groupId) || false;
  const [products, setProducts] = useState(productsProp);
  const [selectedProject, setSelectedProject] = useState({ projectId, projectName: '' });
  const { svocID, customerType } = useThdCustomer() || {};
  const isMultiAdd = products?.length > 1 || products?.some((item) => (!!item?.purchaseIntent && item?.itemType === 'intent'));
  const itemId = selectedProductDetails?.itemId || products[0]?.itemId || productPodItemData?.itemId;
  const [isSearchResults, setIsSearchResults] = useState(Boolean(productPodItemData?.searchKeyword));

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: isMultiAdd || !products[0]?.itemId
  });

  const pageSize = 25;
  const [page, setPage] = useState(1);
  const [loadedProjects, setLoadedProjects] = useState([]);

  const { product: singleProductData = {} } = data || selectedProductDetails?.productData || {};
  const {
    storeSkuNumber, omsThdSku, specialOrderSku
  } = singleProductData?.identifiers
    || selectedProductDetails?.productData?.identifiers
    || selectedProductDetails?.identifiers
    || {};
  const singleItemSku = storeSkuNumber || omsThdSku || specialOrderSku;
  const { pricing } = singleProductData;
  const basePrice = pricing?.value;
  const quantity = selectedProductDetails?.quantity || products[0]?.quantity;

  // Bundle detection — decompose CONFIGURABLE_BUNDLE into individual component items
  const selectedBundleItems = selectedProductDetails?.productData?.product?.bundleItems
    || selectedProductDetails?.productData?.product?.bundleSpecificationDetails?.components;
  const productType = singleProductData?.identifiers?.productType
    || selectedProductDetails?.productData?.identifiers?.productType;
  const isItemBundle = productType === 'CONFIGURABLE_BUNDLE'
    && (singleProductData?.bundleItems?.length > 0 || selectedBundleItems?.length > 0);
  const bundleItems = singleProductData?.bundleItems || selectedBundleItems || [];
  const bundledProducts = isItemBundle ? bundleItems.map((item) => ({
    itemId: item.id || item.defaultProductId,
    quantity: item.quantity || quantity || 1,
    sku: item.id || item.defaultProductId,
    omsId: item.id || item.defaultProductId,
    itemType: 'catalog'
  })) : [];
  const { projectData, projectLoading, projectRefetch } = useProjectDetails({
    projectId: selectedProject.projectId
  });
  const {
    projectName = '',
    propertyType,
    addresses,
    defaultListId,
    defaultListMutationKey,
    itemGroups,
    assortmentZip,
    preferredStore,
  } = projectData?.projectDetails || {};
  const assortmentZipId = addresses?.find((address) => address.addressType === 'ASSORTMENT_ZIP')?.id || null;
  
  // Derive isProjectEmpty for gating "Choose for me" button (DSCVR-4801)
  const isProjectEmpty = !projectLoading && (
    !Array.isArray(itemGroups) || itemGroups.every((group) => !group.products?.length)
  );

  const bathroomQuestionnaireMaterials = useMemo(() => (
    (itemGroups || []).map((group) => ({
      groupId: group.id,
      groupName: group.groupName,
      items: (group.products || []).map((product) => ({
        quantity: product.quantity || 1,
        intent: product.purchaseIntent,
      })),
    }))
  ), [itemGroups]);
  const [keyword, setKeyword] = useState(productPodItemData?.searchKeyword || '');
  const selectedGroupId = useRef(null);
  const {
    PROJECT_SELECT_PROJECT,
    DEFAULT,
    PROJECT_CREATE_PROJECT,
    PROJECT_ZERO_STATE,
    MINI_PIP_BASE,
    MINI_PIP_PRODUCT_OVERVIEW,
    MINI_PIP_PRODUCT_SPECS,
    MINI_PIP_PRODUCT_REVIEWS,
    MINI_PIP_FULL,
    LOADING,
    MY_STORE,
    DELIVERY_ZIP,
    STARTER_LISTS,
    GENERATED_LIST_INTENTS_PREVIEW,
    AI_LOADING_ANIMATION,
    CREATE_PROJECT_PREVIEW,
    BATHROOM_QUESTIONNAIRE
  } = SCREENS;

  const { handleCreateNewIntents } = useAddNewIntentsProject({
    projectId: selectedProject.projectId,
    projectRefetch,
    defaultListMutationKey,
    defaultListId
  });
  const aiListBuilderData = {
    refId: selectedProject.projectId,
    title: selectedProject?.projectName,
    onNewIntentsSave: handleCreateNewIntents,
    refType: 'PROJECT',
    materials: bathroomQuestionnaireMaterials
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-project.ready');
  }, []);

  useEffect(() => {
    if (selectedProject?.projectId) {
      projectRefetch();
    }
  }, [selectedProject?.projectId]);

  useEffect(() => {
    if (productsProp?.length > 0) {
      if (productsProp?.length > 1) {
        const productData = productsProp?.map((product) => {
          if (isItemIntent(product)) {
            return product;
          }

          const loadedProductData = loadedProductsData.find((loadedProduct) => {
            return loadedProduct?.itemId === product?.itemId;
          });

          const sku = getValidSku(product?.sku, product?.identifiers || loadedProductData?.identifiers);

          return { ...product, sku: sku || null };
        });
        setProducts(productData);
      } else {
        setProducts(productsProp);
      }
    }
  }, [productsProp, loadedProductsData]);

  const { projects, error, refetch, loading } = useProjects({
    page,
    pageSize,
    accountId: svocID,
    filter: [],
    sort: []
  });

  const projectsCount = projects?.projects?.totalProjects || 0;
  const productsCount = products?.length || 0;

  useEffect(() => {
    refetch();
  }, [svocID, page]);

  const [hasLoadedProjects, setHasLoadedProjects] = useState(false);
  const isResettingProjects = useRef(false);

  useEffect(() => {
    if (initialLoad && hasLoadedProjects) {
      isResettingProjects.current = true;
      setPage(1);
      setLoadedProjects([]);
      setHasLoadedProjects(false);
      refetch();
    }
  }, [initialLoad]);

  useEffect(() => {
    if (!loading) {
      setLoadedProjects((prevLoadedProjects) => {
        return [...prevLoadedProjects, ...projects?.projects?.projects || []];
      });
      setHasLoadedProjects(true);
      isResettingProjects.current = false;
    }
  }, [projects]);

  useEffect(() => {
    // `copyListToNewProject` drives its own screen sequence (copy-list picker → create-project
    // preview). This zero-state routing would otherwise clobber the preview with PROJECT_ZERO_STATE
    // once the copied items populate `products`, so skip it entirely for that flow.
    if (!initialLoad || !productsCount || isResettingProjects.current || copyListToNewProject) return;

    const isLoading = loading || !hasLoadedProjects;
    const projectsExist = Boolean(loadedProjects?.length);

    if (isLoading && !projectsExist) {
      setScreen(LOADING);
    } else if (projectsExist) {
      setScreen(PROJECT_SELECT_PROJECT);
      setInitialLoad(false);
      triggerAnalytics(ANALYTICS_SELECT_PROJECT, {});
    } else if (!isLoading && !projectsExist) {
      const hasProducts = products && products.length > 0;
      setScreen(hasProducts ? PROJECT_ZERO_STATE : PROJECT_CREATE_PROJECT);
      setInitialLoad(false);
      if (hasProducts) {
        triggerAnalytics(TRACK.NO_PROJECTS_OVERLAY);
      }
    }
  }, [loading, loadedProjects, initialLoad, products, hasLoadedProjects]);

  useEffect(() => {
    if (screen === PROJECT_ZERO_STATE) {
      return;
    }
    if (products?.length > 0 && projectLoading) {
      setScreen(LOADING);
    } else if (products?.length > 0 && selectedProject?.projectId && !projectLoading
      && userSelectedProject.current) {
      addItemToProject({ groupName: 'Unassigned' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectLoading, selectedProject]);

  // Handle product pod "Add to Project" click in the add-materials flow
  useEffect(() => {
    if (selectedProductDetails?.itemId && selectedProject?.projectId && !projectLoading
      && screen === DEFAULT && !isIntent) {
      addItemToProject({ groupName: 'Unassigned' });
      setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductDetails?.itemId, screen, projectLoading]);

  const updateSelectedGroupId = (newGroupId) => {
    selectedGroupId.current = newGroupId;
  };

  const singleProduct = isItemBundle
    ? bundledProducts
    : [{ itemId, quantity, sku: singleItemSku, itemType: 'catalog' }];
  const singleProductAnalytic = singleProduct.map((product) => ({
    quantity: product.quantity,
    sku: product.itemId,
    price: basePrice,
    aiQuantityEstimate: product?.aiQuantityEstimate ? 'yes' : 'no'
  }));

  const multipleProduct = products.map((prod) => {
    const isProductIntent = isItemIntent(prod);

    if (isProductIntent) {
      // PRO41-1848 (repair round 2): allow-list the fields the projects orchestration's
      // `Items` input actually supports for an intent item, instead of a blind rest-spread.
      // Live wire evidence confirmed the BE (pro-projects-orchestration-api) has NEITHER an
      // `intentResolved` field (ValidationError) NOR an `intentId` field (ValidationError:
      // "field name 'intentId' is not defined for input object type 'Items'") — a copy
      // creates a brand-new item, so resolution state is represented purely via
      // `itemType: 'intent'` with no id field carried over at all.
      return {
        itemType: 'intent',
        purchaseIntent: prod.purchaseIntent,
        quantity: prod.quantity
      };
    }
    return {
      itemId: prod.itemId,
      quantity: prod.quantity,
      sku: getValidSku(prod.sku, prod.identifiers) || null,
      itemType: 'catalog'
    };
  }).filter((prod) => isItemIntent(prod) || prod.sku);
  const multipleProductAnalytic = multipleProduct.map((product) => ({
    quantity: product.quantity,
    sku: product.itemId,
    price: product?.pricing?.value,
    aiQuantityEstimate: product?.aiQuantityEstimate ? 'yes' : 'no'
  }));

  const [addItem] = useLazyDataModel('updateMaterials', {
    variables: {
      listId: defaultListId,
      mutationKey: defaultListMutationKey,
      operation: 'ADD_ITEM',
      groupName: selectedGroup,
      items: isMultiAdd ? multipleProduct : singleProduct
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const copyLocationRef = useRef('');

  const handleAddItemsFromCopyList = async ({ productsGotten, preview = false, copyLocation = '' }) => {
    copyLocationRef.current = copyLocation;
    // `copyListToNewProject` consumers (e.g. the Projects empty-landing "Start with an existing
    // list" card) open copy-from-list with no target project, so there's no listId to update —
    // create the project first via the preview screen instead of firing updateMaterials with a
    // null listId. Other consumers leave the flag false and keep the existing add-to-project path.
    if (preview || copyListToNewProject) {
      setProducts(productsGotten);
      setScreen(CREATE_PROJECT_PREVIEW);
      return;
    }
    const response = await addItem({
      variables: {
        items: productsGotten,
        groupName: 'Unassigned'
      }
    });
    if (response?.data?.updateMaterials?.groupId) {
      setShowSuccessMessage(true);
      setScreen(DEFAULT);
      projectRefetch();
    }
  };

  const [resolveIntent] = useLazyDataModel('updateMaterials', {
    variables: {
      listId: defaultListId,
      mutationKey: defaultListMutationKey,
      operation: 'RESOLVE_INTENT',
      groupId: [productPodItemData?.groupId]
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const handleResolveIntent = async ({ omsId, sku }) => {
    const { onIntentResolved } = productPodItemData || {};
    try {
      if (onIntentResolved) {
        await onIntentResolved({
          itemIds: [omsId],
          products: [{ itemId: omsId, identifiers: { storeSkuNumber: sku } }]
        });
      } else {
        const response = await resolveIntent({
          variables: {
            items: [
              {
                id: productPodItemData?.id,
                omsId,
                sku,
              }
            ]
          }
        });
        if (response?.data?.updateMaterials?.groupId) {
          setScreen(DEFAULT);
          projectRefetch();
        }
      }
      handleClose();
    } catch (errorCatched) {
      // eslint-disable-next-line no-console
      console.error('Error resolving intent:', errorCatched);
    }
  };

  const fireAddItemAnalytics = ({ type, items }) => {
    const eventName = type === 'NEW_GROUP' ? ADD_ITEMS_PROJECT_NEW_GROUP : ADD_ITEMS_PROJECT_EXISTING_GROUP;
    const analyticInfo = {
      id: selectedProject?.projectId,
      type: propertyType ? 'y' : 'n',
      desc: projectName ? 'y' : 'n',
      address: addresses?.[0]?.addressLine1 ? 'y' : 'n',
      zip: addresses?.[0]?.zipCode ? 'y' : 'n',
      items
    };
    LIFE_CYCLE_EVENT_BUS.trigger(eventName, analyticInfo);

    triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
      name: 'project add',
      location: '',
      items,
      projectId: selectedProject?.projectId,
      uniqueSkuCount: new Set(items.map((i) => i.sku)).size,
      projectAddTotal: items.reduce(
        (total, item) => total + ((item.price || 0) * (item.quantity || 1)), 0
      ),
    });
  };

  const getLastBundleResponse = (index, totalItems, addResult, currentResponse) => {
    if (index === totalItems - 1) {
      return addResult;
    }
    return currentResponse;
  };

  const addItemToProject = async ({ type = 'OLD_GROUP', groupName }) => {
    let response;

    if (isItemBundle && bundledProducts.length > 0 && !isMultiAdd) {
      // For bundle products, add each component item sequentially with mutationKey chaining
      let currentMutationKey = defaultListMutationKey;
      for (let i = 0; i < bundledProducts.length; i += 1) {
        const bundleItem = bundledProducts[i];
        // eslint-disable-next-line no-await-in-loop
        const addResult = await addItem({
          variables: {
            mutationKey: currentMutationKey,
            groupName: groupName || selectedGroup,
            items: [{
              itemId: bundleItem.itemId,
              omsId: bundleItem.omsId,
              sku: bundleItem.sku,
              quantity: bundleItem.quantity,
              itemType: 'catalog'
            }]
          }
        });
        currentMutationKey = addResult?.data?.updateMaterials?.mutationKey || currentMutationKey;
        response = getLastBundleResponse(i, bundledProducts.length, addResult, response);
      }
    } else {
      response = await addItem({
        variables: {
          groupName: groupName || selectedGroup
        }
      });
    }

    if (response?.data?.updateMaterials?.groupId) {
      try {
        localStorage.setItem(LAST_USED_PROJECT_KEY, JSON.stringify({
          projectId: selectedProject.projectId,
          projectName: selectedProject.projectName
        }));
      } catch (e) { /* graceful fail in incognito */ }
      setShowSuccessMessage(true);
      setScreen(DEFAULT);
      projectRefetch();
    }
    const hasProducts = singleProduct?.length > 0 || multipleProduct?.length > 0;
    if (hasProducts && isProjectCreated.current === true) {
      fireNewProjectAnalytics();
    } else if (hasProducts) {
      const items = isMultiAdd ? multipleProductAnalytic : singleProductAnalytic;
      fireAddItemAnalytics({ type, items });
    }
    return response;
  };

  const handleProjectCreation = async (response) => {
    if (response?.id) {
      userSelectedProject.current = true;
      setSelectedProject({
        projectId: response?.id,
        projectName: response?.projectName
      });
    }
    isProjectCreated.current = true;
  };

  const handleZeroStateSuccess = () => {
    setShowSuccessMessage(true);
    // Screen stays on PROJECT_ZERO_STATE to show success state
  };

  // Success handler for the create-project preview reached from the copy-list-to-new-project flow.
  // When the consumer supplied an onProjectCreated callback, hand off so it can navigate to the new
  // project like the other create-project entry points; otherwise fall back to the in-drawer success.
  const handleCopyListProjectCreated = (created = {}) => {
    if (copyListToNewProject && onProjectCreated) {
      onProjectCreated({ id: created.projectId, projectName: created.projectName });
      return;
    }
    handleZeroStateSuccess();
  };

  const fireNewProjectAnalytics = () => {
    const items = isMultiAdd ? multipleProductAnalytic : singleProductAnalytic;
    const payload = {
      id: selectedProject?.projectId,
      type: propertyType ? 'y' : 'n',
      desc: projectName ? 'y' : 'n',
      address: addresses?.[0]?.addressLine1 ? 'y' : 'n',
      zip: addresses?.[0]?.zipCode ? 'y' : 'n',
      items,
      copyAnalytic: addToType === ADD_TO_TYPE.PROJECT ? COPY_FROM_PROJECT : COPY_FROM_LIST
    };
    LIFE_CYCLE_EVENT_BUS.trigger(ADD_ITEM_NEW_PROJECT, payload);

    triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
      name: 'new project project add',
      location: copyLocationRef.current || '',
      items,
      projectId: selectedProject?.projectId,
      uniqueSkuCount: new Set(items.map((i) => i.sku)).size,
      projectAddTotal: items.reduce(
        (total, item) => total + ((item.price || 0) * (item.quantity || 1)), 0
      ),
      projectStarter: copyLocationRef.current ? 'copy from your lists' : '',
      type: propertyType ? 'y' : 'n',
      desc: projectName ? 'y' : 'n',
      address: addresses?.[0]?.addressLine1 ? 'y' : 'n',
      zip: addresses?.[0]?.zipCode ? 'y' : 'n',
    });
  };

  useEffect(() => {
    if (itemGroups) {
      const newGroupId = itemGroups.find((group) => group.groupName === selectedGroup)?.id;
      if (newGroupId) {
        updateSelectedGroupId(newGroupId);
      }
    }
  }, [selectedGroup]);

  const onSearch = (searchKeyword) => {
    setKeyword(searchKeyword);
  };

  // Simple screen-to-screen back navigation map
  const backNavigationMap = {
    [MINI_PIP_PRODUCT_OVERVIEW]: { target: MINI_PIP_BASE },
    [MINI_PIP_PRODUCT_SPECS]: { target: MINI_PIP_BASE },
    [MINI_PIP_PRODUCT_REVIEWS]: { target: MINI_PIP_BASE },
    [MY_STORE]: { target: DEFAULT },
    [DELIVERY_ZIP]: { target: DEFAULT },
    [AI_LOADING_ANIMATION]: { target: DEFAULT },
    [BATHROOM_QUESTIONNAIRE]: { target: DEFAULT },
    [STARTER_LISTS]: { target: DEFAULT, clearSearch: true },
  };

  const handleBackPress = () => {
    triggerAnalytics(TRACK.ADD_TO_OVERLAY_EXIT, { screen });

    // Special cases requiring custom logic
    if (screen === DEFAULT && isSearchResults) {
      setIsSearchResults(false);
      return;
    }
    if (screen === PROJECT_CREATE_PROJECT && !!loadedProjects?.length) {
      setScreen(PROJECT_SELECT_PROJECT);
      return;
    }
    if (addToType === 'Cart' && !!atcDrawerType) {
      setScreen(MINI_PIP_FULL);
      setAtcDrawerType('');
      return;
    }
    if (screen === MINI_PIP_BASE) {
      setScreen(getMiniPipBackTarget(products));
      setSelectedGroup(null);
      setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
      return;
    }
    if (screen === AI_LOADING_ANIMATION && previousScreen === BATHROOM_QUESTIONNAIRE) {
      setScreen(BATHROOM_QUESTIONNAIRE);
      return;
    }
    const intentsPreviewTarget = previousScreen === AI_LOADING_ANIMATION ? DEFAULT : previousScreen;
    if (screen === GENERATED_LIST_INTENTS_PREVIEW) {
      setScreen(intentsPreviewTarget);
      return;
    }
    if (screen === CREATE_PROJECT_PREVIEW) {
      setScreen(previousScreen || SCREENS.COPY_LIST_SUMMARY);
      return;
    }

    // Simple navigations from map
    const nav = backNavigationMap[screen];
    if (!nav) return;
    setScreen(nav.target);
    if (nav.clearSearch) setIsSearchResults(false);
  };

  const showBackHandler = ((shouldShowBack({
    screen,
    addToType,
    PROJECT_SELECT_PROJECT,
    PROJECT_CREATE_PROJECT,
    PROJECT_ZERO_STATE,
    DEFAULT,
    loadedProjects,
    isDisplayOnly,
    MINI_PIP_BASE
  }) && showBackButton) || isSearchResults);

  return (
    <>
      {!screen?.startsWith('COPY_LIST') && (
        <DrawerHeader
          data-testid="add-to-drawer-header"
          title={getDrawerTitle({
            screen,
            showSuccessMessage,
            isIntent,
            HEADER_TEXT,
            DEFAULT,
            isDisplayOnly
          })}
          onClose={handleClose}
          onBack={showBackHandler ? handleBackPress : null}
          {...(!showBackHandler && isDisplayOnly ? { startAdornment: <div className="sui-w-10" /> } : {})}
        />
      )}
      {screen === LOADING && (
        <AddToLoading />
      )}
      {/* DefaultScreen must stay mounted during AI_LOADING_ANIMATION so the mutation hook survives */}
      {(screen === DEFAULT || screen === AI_LOADING_ANIMATION || !screen) && (
        <div style={{ display: screen === AI_LOADING_ANIMATION ? 'none' : 'contents' }}>
          <DefaultScreen
            keyword={keyword}
            productPodItemData={productPodItemData}
            onSearch={onSearch}
            handleClose={handleClose}
            projectId={selectedProject.projectId}
            selectedGroup={selectedGroup}
            selectedGroupId={selectedGroupId}
            products={products}
            selectedProject={selectedProject}
            customerType={customerType}
            projectDetails={projectData?.projectDetails}
            aiListBuilderData={aiListBuilderData}
            isIntent={isIntent}
            handleResolveIntent={handleResolveIntent}
            isSearchResults={isSearchResults}
            setIsSearchResults={setIsSearchResults}
          />
        </div>
      )}
      {screen === AI_LOADING_ANIMATION && (
        <AILoadingAnimation />
      )}
      {screen === BATHROOM_QUESTIONNAIRE && (
        <BathroomQuestionnaire
          name={aiListBuilderData.title}
          materials={aiListBuilderData.materials}
          refType={aiListBuilderData.refType}
          refId={aiListBuilderData.refId}
        />
      )}
      {screen === PROJECT_CREATE_PROJECT && (
        <CreateAProjectContent onCreate={handleProjectCreation} handleClose={handleClose} isAddToDrawer />
      )}
      {screen === PROJECT_ZERO_STATE && (
        <ZeroProjectState
          products={products}
          onProjectCreatedAndItemAdded={handleZeroStateSuccess}
          singleProduct={singleProduct}
          multipleProduct={multipleProduct}
          basePrice={basePrice}
        />
      )}
      {screen === CREATE_PROJECT_PREVIEW && (
        <QueryProvider cacheKey="create-project-preview" dataSource="catalog">
          <CreateProjectPreview
            products={products}
            singleProduct={singleProduct}
            multipleProduct={multipleProduct}
            onProjectCreatedAndItemAdded={handleCopyListProjectCreated}
            copyLocation={copyLocationRef.current}
          />
        </QueryProvider>
      )}
      {screen === PROJECT_SELECT_PROJECT && (
        <SelectProject
          products={products}
          singleProduct={singleProduct}
          multipleProduct={multipleProduct}
          projects={loadedProjects}
          projectsExist={!!loadedProjects?.length}
          setSelectedProject={(proj) => {
            userSelectedProject.current = true;
            setSelectedProject(proj);
          }}
          pageSize={pageSize}
          projectsCount={projectsCount}
          loading={loading}
          setInitialLoad={setInitialLoad}
          setPage={setPage}
          page={page}
          onInlineCreationSuccess={handleZeroStateSuccess}
        />
      )}

      {
        screen?.startsWith('MINI_PIP') && (
          <MiniPip itemId={itemId} isIntent={isIntent} handleResolveIntent={handleResolveIntent} isDisplayOnly={isDisplayOnly} />
        )
      }
      {
        screen?.startsWith('COPY_LIST') && (
          <CopyList
            onClose={handleClose}
            onUpdateProjects={handleAddItemsFromCopyList}
            initialProducts={products}
            listId=""
            onMaterialsAdded={() => {}}
          />
        )
      }
      {
        screen === MY_STORE && (
          <MyStoreForm
            projectId={projectId}
            projectName={projectName}
            assortmentZip={assortmentZip}
            storeId={preferredStore}
            onUpdate={onLocalizationUpdate}
          />
        )
      }
      {
        screen === DELIVERY_ZIP && (
          <DeliveryZipcodeForm
            projectId={projectId}
            projectName={projectName}
            assortmentZipCode={assortmentZip}
            assortmentZipId={assortmentZipId}
            preferredStore={preferredStore}
            onUpdate={onLocalizationUpdate}
          />
        )
      }
      {
        screen === GENERATED_LIST_INTENTS_PREVIEW && (
          <QueryProvider cacheKey="intents-preview">
            <IntentsPreview
              onNewIntentsSave={aiListBuilderData.onNewIntentsSave}
              refId={aiListBuilderData.refId}
              refType={aiListBuilderData.refType}
              projectId={selectedProject.projectId}
              listTitle={aiListBuilderData.title}
              isProjectEmpty={isProjectEmpty}
            />
          </QueryProvider>
        )
      }
      {
        screen === STARTER_LISTS && (
          <StarterLists aiListBuilderData={aiListBuilderData} />
        )
      }
    </>
  );
};

AddToProject.displayName = 'AddToProject';

AddToProject.propTypes = {
  projectId: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  initialLoad: PropTypes.bool.isRequired,
  setInitialLoad: PropTypes.func.isRequired,
  showBackButton: PropTypes.bool,
  productPodItemData: PropTypes.shape({
    itemId: PropTypes.string,
    id: PropTypes.string,
    quantity: PropTypes.number,
    searchKeyword: PropTypes.string,
    groupId: PropTypes.string
  }),
  aiListBuilderData: PropTypes.shape({
    onNewIntentsSave: PropTypes.func,
    title: PropTypes.string,
    refId: PropTypes.string,
    refType: PropTypes.string,
    materials: PropTypes.arrayOf(PropTypes.shape({
      groupName: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.shape({
        quantity: PropTypes.number,
        product: PropTypes.string,
      }))
    }))
  }),
  loadedProductsData: PropTypes.arrayOf(PropTypes.shape({})),
  isDisplayOnly: PropTypes.bool,
};

AddToProject.defaultProps = {
  projectId: null,
  products: [],
  showBackButton: true,
  productPodItemData: {},
  aiListBuilderData: {},
  isDisplayOnly: false
};

AddToProject.dataModel = extend({
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    bundleFlag: bool(),
    bundleItems: arrayOf(shape({
      id: string(),
      quantity: number()
    })),
    identifiers: shape({
      brandName: string(),
      productType: string(),
      productLabel: string(),
      storeSkuNumber: string(),
      omsThdSku: string(),
      specialOrderSku: string()
    }),
    info: shape({
      productDepartment: string(),
      classNumber: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string()
      }))
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  })
},
{
  updateMaterials: params({
    listId: string().isRequired(),
    mutationKey: string().isRequired(),
    operation: customType('MaterialsOperationType').enum(
      ['EDIT_GROUP_NAME', 'DELETE_GROUP', 'ADD_ITEM', 'DELETE_ITEM', 'UPDATE_QUANTITY', 'REARRANGE', 'RESOLVE_INTENT']
    ),
    groupName: string(),
    groupId: arrayOf(string()),
    items: arrayOf(customType('Items').shape({
      itemId: string(),
      id: string(),
      omsId: string(),
      quantity: number(),
      sku: string(),
      itemType: string()
    }))
  }).mutation().shape({
    groupId: string(),
    mutationKey: string()
  })
},
DefaultScreen,
projectsModel,
CreateAProjectContent,
DeliveryZipcodeForm,
MyStoreForm,
useAddNewIntentsProject,
projectDetailsModel,
ZeroProjectState,
CopyList,
CreateProjectPreview
);
